import { subject } from '@casl/ability';
import { useSiteQuery } from 'graphql/useSiteQuery.am';
import { useAbilities } from 'context/AbilityProvider/useAbilities';
import { useGuaranteedAccount } from 'hooks/useAccount.am';
import { useGuaranteedCurrentUser } from 'hooks/useCurrentUser';
import { useCustomerPortalUrl } from 'hooks/useCustomerPortalUrl.am';
import { SuspenseWithFallback } from 'lazy-routes';
import { DateTime } from 'luxon';
import { useMatch, useParams } from 'react-router';
import { AuthenticatedNavBar } from 'turnip/AuthenticatedNavBar/AuthenticatedNavBar';
import { Banner } from 'turnip/Banner/Banner';
import { CenteredPageLoadingCarrot } from 'turnip/LoadingCarrot/LoadingCarrot';
import { Offset } from 'turnip/Offset/Offset';
import {
  SingleSiteNav,
  type SingleSiteNavProps,
} from 'turnip/SingleSiteNav/SingleSiteNav';
import { Stack } from 'turnip/Stack/Stack';
import { StyledLink } from 'turnip/StyledLink/StyledLink';
import type { Maybe } from 'types/Utilities';
import { linkOrSupportLink } from 'utils/linkOrSupportLink';

export function PageWithAuthenticatedNavs({
  disableSingleSiteNav,
  children,
}: {
  disableSingleSiteNav?: boolean;
  children: React.ReactNode;
}) {
  const {
    activeAccount,
    accounts,
    avatarUrl,
    carrotVersion,
    currentSubscription,
    wordpressUserId,
    superAdmin,
  } = useGuaranteedCurrentUser();
  const params = useParams();
  const siteId = params.siteId && parseInt(params.siteId);

  const agencyAccountId = accounts.find((account) => account.isAgency)?.id;

  const suspenseChildren = (
    <SuspenseWithFallback title="Loading page">{children}</SuspenseWithFallback>
  );

  const isSuperAdminRoute = useMatch({
    path: '/super/',
    end: false,
  });
  const shouldRenderSingleSiteNav = siteId && !disableSingleSiteNav;
  const bodyTopOffset =
    isSuperAdminRoute || shouldRenderSingleSiteNav ? 14 : 24;

  return (
    <>
      <AuthenticatedNavBar
        accounts={accounts}
        activeAccountId={activeAccount?.id}
        avatarUrl={avatarUrl}
        agencyAccountId={agencyAccountId}
      />

      {activeAccount?.owner.carrotAccountStatus === 'active_past_due' && (
        <PastDueBanner
          pastDueCancelAtDate={currentSubscription?.pastDueCancelAtDate}
          wordpressUserId={wordpressUserId}
        />
      )}

      <Offset top={bodyTopOffset}>
        {shouldRenderSingleSiteNav ? (
          <SideNav
            siteId={siteId}
            isSuperAdmin={!!superAdmin}
            isV3User={carrotVersion === 3}
          >
            {suspenseChildren}
          </SideNav>
        ) : (
          suspenseChildren
        )}
      </Offset>
    </>
  );
}

function SideNav({
  siteId,
  isSuperAdmin,
  isV3User,
  children,
}: React.PropsWithChildren<
  Pick<SingleSiteNavProps, 'siteId' | 'isSuperAdmin' | 'isV3User'>
>) {
  const account = useGuaranteedAccount();
  const site = account.sites?.find((blog) => blog.platformSiteId === siteId);

  const abilities = useAbilities();
  const siteSubject = subject('Site', { platform_site_id: siteId });
  const canUpdateSite = abilities.can('update', siteSubject);
  const canDeleteSite = abilities.can('destroy', siteSubject);

  /**
   * Fall back to querying the blog data if it wasn't found in the account
   * data. This should only happen if the route doesn't contain an accountId
   * and the site isn't in the user's active account.
   *
   * TODO: ALUM - remove this once all routes have an accountId in the URL
   */
  const params = useParams();
  const accountIdRouteParam = params.accountId;
  const siteQuery = useSiteQuery(accountIdRouteParam ? undefined : siteId);
  if (siteQuery.isEnabled && siteQuery.isPending) {
    return <CenteredPageLoadingCarrot title="Loading site data" />;
  }
  const accountId = accountIdRouteParam
    ? parseInt(accountIdRouteParam)
    : siteQuery.data?.site?.accountId;
  const blogData = site ?? siteQuery.data?.site;
  const subdomain = blogData?.subdomain;

  return (
    <Stack direction="horizontal">
      {subdomain && (
        <div className="hidden w-52 shrink-0 pl-9 pr-5 lg:block">
          <SingleSiteNav
            siteId={siteId}
            domain={subdomain}
            isSuperAdmin={isSuperAdmin}
            owner={blogData.owner}
            category={blogData.category}
            registeredAt={blogData.registeredAt}
            accountId={accountId}
            isV3User={isV3User}
            blockTheme={blogData.blockTheme}
            canUpdateSite={canUpdateSite}
            canDeleteSite={canDeleteSite}
          />
        </div>
      )}

      <div className="w-full min-w-0">{children}</div>
    </Stack>
  );
}

function PastDueBanner({
  pastDueCancelAtDate,
  wordpressUserId,
}: {
  pastDueCancelAtDate: Maybe<string>;
  wordpressUserId: number;
}) {
  const { customerPortalUrl, loading } = useCustomerPortalUrl(wordpressUserId);
  if (loading) return null;

  const [linkHref, linkText] = linkOrSupportLink(
    customerPortalUrl,
    'update your billing details',
    'contact support'
  );
  const bannerLink = (
    <StyledLink variant="contextual" to={linkHref}>
      {linkText}
    </StyledLink>
  );

  const formattedCancelDate = pastDueCancelAtDate
    ? DateTime.fromISO(pastDueCancelAtDate).toLocaleString(DateTime.DATE_FULL)
    : null;
  const dateText = pastDueCancelAtDate
    ? ` before ${formattedCancelDate} `
    : ' ';
  const bannerEndText = 'so you don’t miss any new leads or deals!';

  if (!customerPortalUrl) {
    return (
      <Banner>
        Please {bannerLink} to update your billing {dateText} {bannerEndText}
      </Banner>
    );
  }

  return (
    <Banner>
      Please {bannerLink}
      {dateText}
      {bannerEndText}
    </Banner>
  );
}
